import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FileSpreadsheet, Search, Plus, Clock, AlertCircle, CheckCircle2, XCircle, Upload, FileText, Filter, Trash2, Pencil } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuotesListPage() {
  const navigate = useNavigate();
  const { hasAnyRole, user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteJustification, setDeleteJustification] = useState('');
  
  // AFM Edit states
  const [editAfmDialogOpen, setEditAfmDialogOpen] = useState(false);
  const [quoteToEditAfm, setQuoteToEditAfm] = useState(null);
  const [newAfmName, setNewAfmName] = useState('');
  const [updatingAfm, setUpdatingAfm] = useState(false);
  
  const isSuperAdmin = user?.roles?.includes('Super Admin');

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'yet_to_initiate', label: 'Yet to Initiate' },
    { value: 'awaiting_payment', label: 'Awaiting Payment / Material' },
    { value: 'rejected', label: 'Payment / Material Rejected' },
    { value: 'pending', label: 'Ready for Work' },
    { value: 'work_in_progress', label: 'Work In Progress' },
    { value: 'work_cancelled', label: 'Cancelled' },
    { value: 'jobsheet_pending', label: 'Jobsheet Pending' },
    { value: 'jobsheet_submitted', label: 'Jobsheet Submitted' },
    { value: 'billing_approved', label: 'Completed' },
    { value: 'billing_rejected', label: 'Jobsheet Rejected' }
  ];

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    let filtered = quotes;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (q) =>
          q.quote_no.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (q.supervisor_name && q.supervisor_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (q.work_description && q.work_description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'yet_to_initiate') {
        filtered = filtered.filter(q => !q.has_requests && (!q.work_status || q.work_status === 'pending'));
      } else if (statusFilter === 'rejected') {
        // Has rejected fund/material requests
        filtered = filtered.filter(q => q.has_rejected_requests && !q.is_ready_for_work && (!q.work_status || q.work_status === 'pending'));
      } else if (statusFilter === 'awaiting_payment') {
        // Has requests but not ready for work yet (approved but not paid) and not rejected
        filtered = filtered.filter(q => q.has_requests && !q.is_ready_for_work && !q.has_rejected_requests && (!q.work_status || q.work_status === 'pending'));
      } else if (statusFilter === 'pending') {
        // Ready for work (has paid fund requests or approved material requests)
        filtered = filtered.filter(q => q.is_ready_for_work && (!q.work_status || q.work_status === 'pending'));
      } else if (statusFilter === 'jobsheet_pending') {
        // Include both work_completed and jobsheet_pending
        filtered = filtered.filter(q => q.work_status === 'jobsheet_pending' || q.work_status === 'work_completed');
      } else {
        filtered = filtered.filter(q => q.work_status === statusFilter);
      }
    }
    
    setFilteredQuotes(filtered);
  }, [searchTerm, statusFilter, quotes]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
      setFilteredQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (quote) => {
    // Check if quote has no requests initiated - show "Yet to initiate"
    if (!quote.has_requests && (!quote.work_status || quote.work_status === 'pending')) {
      return (
        <Badge className="bg-gray-100 text-gray-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Yet to initiate
        </Badge>
      );
    }
    
    // Check if quote has rejected fund/material requests - show "Payment / Material Rejected"
    if (quote.has_rejected_requests && !quote.is_ready_for_work && (!quote.work_status || quote.work_status === 'pending')) {
      return (
        <Badge className="bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Payment / Material Rejected
        </Badge>
      );
    }
    
    // Check if quote has requests but not yet ready for work (approved but not paid)
    if (quote.has_requests && !quote.is_ready_for_work && (!quote.work_status || quote.work_status === 'pending')) {
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Clock className="h-3 w-3 mr-1" />
          Awaiting Payment / Material
        </Badge>
      );
    }
    
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-800', label: 'Ready for Work', icon: Clock },
      work_in_progress: { className: 'bg-blue-100 text-blue-800', label: 'Work In Progress', icon: Clock },
      work_completed: { className: 'bg-orange-100 text-orange-800', label: 'Jobsheet Pending', icon: Upload },  // Same as jobsheet_pending
      work_cancelled: { className: 'bg-red-100 text-red-800', label: 'Cancelled', icon: XCircle },
      jobsheet_pending: { className: 'bg-orange-100 text-orange-800', label: 'Jobsheet Pending', icon: Upload },
      jobsheet_submitted: { className: 'bg-purple-100 text-purple-800', label: 'Jobsheet Submitted', icon: FileText },
      billing_approved: { className: 'bg-green-100 text-green-800', label: 'Completed', icon: CheckCircle2 },
      billing_rejected: { className: 'bg-red-100 text-red-800', label: 'Jobsheet Rejected', icon: XCircle }
    };
    const config = variants[quote.work_status] || variants.pending;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Check if a quote has related data (fund/material requests)
  const hasRelatedData = (quote) => {
    if (!quote) return false;
    return quote.has_requests;
  };

  const handleDeleteClick = (quote) => {
    setQuoteToDelete(quote);
    setDeleteJustification('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quoteToDelete) return;
    
    // Validate justification if quote has related data
    if (hasRelatedData(quoteToDelete) && deleteJustification.trim().length < 10) {
      toast.error('Please provide a justification of at least 10 characters');
      return;
    }
    
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (deleteJustification.trim()) {
        params.append('justification', deleteJustification.trim());
      }
      
      await axios.delete(`${API}/quotes/${quoteToDelete.id}?${params.toString()}`);
      toast.success(`Quote ${quoteToDelete.quote_no} and all related data deleted successfully`);
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
      setDeleteJustification('');
      fetchQuotes(); // Refresh the list
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    } finally {
      setDeleting(false);
    }
  };

  // AFM Edit handlers
  const handleEditAfmClick = (quote) => {
    setQuoteToEditAfm(quote);
    setNewAfmName(quote.afm_name || '');
    setEditAfmDialogOpen(true);
  };

  const handleUpdateAfm = async () => {
    if (!quoteToEditAfm || !newAfmName.trim()) {
      toast.error('Please enter an AFM name');
      return;
    }
    
    setUpdatingAfm(true);
    try {
      await axios.put(`${API}/quotes/${quoteToEditAfm.id}/update-afm?afm_name=${encodeURIComponent(newAfmName.trim())}`);
      toast.success(`AFM Name updated successfully for quote ${quoteToEditAfm.quote_no}`);
      setEditAfmDialogOpen(false);
      setQuoteToEditAfm(null);
      setNewAfmName('');
      fetchQuotes(); // Refresh the list
    } catch (error) {
      console.error('Update AFM error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update AFM name');
    } finally {
      setUpdatingAfm(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-lg">Loading quotes...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="space-y-6" data-testid="quotes-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quotes
            </h1>
            <p className="text-muted-foreground mt-2">
              {filteredQuotes.length} quote(s) found
            </p>
          </div>
          {hasAnyRole(['Key Account Manager', 'Super Admin']) && (
            <Button onClick={() => navigate('/quotes/upload')} data-testid="upload-quote-button">
              <Plus className="mr-2 h-4 w-4" />
              Upload Quote
            </Button>
          )}
        </div>

        {/* Search and Filter */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by quote number, supervisor, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="w-full sm:w-[220px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Quotes Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="quotes-table">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Quote No</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Supervisor</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Store Address</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Work Description</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">AFM</th>
                  {isSuperAdmin && (
                    <th className="px-6 py-4 text-center text-sm font-medium text-muted-foreground">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 9 : 8} className="px-6 py-12 text-center text-muted-foreground">
                      No quotes found
                    </td>
                  </tr>
                ) : (
                  filteredQuotes.map((quote) => (
                    <tr
                      key={quote.id}
                      className="hover:bg-muted/50 transition-colors"
                      data-testid={`quote-row-${quote.id}`}
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-medium">{quote.quote_no}</span>
                      </td>
                      <td className="px-6 py-4 text-sm whitespace-nowrap">
                        {new Date(quote.quote_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">{quote.supervisor_name}</td>
                      <td className="px-6 py-4 text-sm max-w-[200px]">
                        <span className="break-words">{quote.store_address || '-'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm max-w-[250px]">
                        <span className="break-words">{quote.work_description}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-mono whitespace-nowrap">
                        ₹{quote.amount_with_management_fee?.toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(quote)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-1">
                          <span>{quote.afm_name}</span>
                          {isSuperAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
                              onClick={() => handleEditAfmClick(quote)}
                              title="Edit AFM Name"
                              data-testid={`edit-afm-${quote.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteClick(quote)}
                            data-testid={`delete-quote-${quote.id}`}
                            title="Delete Quote"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Quote
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete quote <strong className="text-foreground">{quoteToDelete?.quote_no}</strong>?
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Warning message */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ This action cannot be undone!
                </p>
                <p className="text-sm text-red-700 mt-1">
                  The following will be permanently deleted:
                </p>
                <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                  <li>Quote and all its details</li>
                  <li>All fund requests</li>
                  <li>All material requests</li>
                  <li>All billing records</li>
                  <li>All uploaded files and jobsheets</li>
                </ul>
              </div>
              
              {/* Justification field */}
              <div className="space-y-2">
                <Label htmlFor="delete-justification" className="text-sm font-medium">
                  Justification for Deletion {hasRelatedData(quoteToDelete) && <span className="text-red-500">*</span>}
                </Label>
                <textarea
                  id="delete-justification"
                  className="w-full min-h-[100px] p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Please provide a reason for deleting this quote..."
                  value={deleteJustification}
                  onChange={(e) => setDeleteJustification(e.target.value)}
                  data-testid="delete-justification-input"
                />
                {hasRelatedData(quoteToDelete) && (
                  <p className="text-xs text-amber-600">
                    This quote has fund/material requests. A justification of at least 10 characters is required.
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setDeleteJustification('');
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleting || (hasRelatedData(quoteToDelete) && deleteJustification.trim().length < 10)}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete Quote'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit AFM Name Dialog */}
        <Dialog open={editAfmDialogOpen} onOpenChange={setEditAfmDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" />
                Update AFM Name
              </DialogTitle>
              <DialogDescription>
                Change the AFM Name for quote <strong className="text-foreground">{quoteToEditAfm?.quote_no}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current-afm" className="text-sm font-medium text-muted-foreground">
                  Current AFM Name
                </Label>
                <div className="p-2 bg-muted rounded-md text-sm">
                  {quoteToEditAfm?.afm_name || '-'}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-afm-name" className="text-sm font-medium">
                  New AFM Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="new-afm-name"
                  value={newAfmName}
                  onChange={(e) => setNewAfmName(e.target.value)}
                  placeholder="Enter new AFM name..."
                  data-testid="new-afm-input"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => {
                  setEditAfmDialogOpen(false);
                  setNewAfmName('');
                }}
                disabled={updatingAfm}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateAfm}
                disabled={updatingAfm || !newAfmName.trim()}
                data-testid="confirm-update-afm-btn"
              >
                {updatingAfm ? 'Updating...' : 'Update AFM'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}
